import BasePeripheral from '@appblocks/codegeneration/Peripherals/_BasePeripheral';
import {
    SettingType, Setting, BlockType, SettingState, DeviceType,
    TibboDataType,
    SettingOption,
} from '@appblocks/codegeneration/types';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';
import Peripherals from '@appblocks/codegeneration/Peripherals';

export default class Ser extends BasePeripheral {
    constructor() {
        super();
        this.id = 'ser';
        this.name = 'Serial Peripheral';
        this.requirements = ['board', 'tios'];
        this.capabilities = ['serial'];
        const settings: Array<Setting> = [
            {
                id: 'PORT',
                name: 'Port',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
                options: [
                    {
                        name: '0',
                        value: '0',
                    },
                    {
                        name: '1',
                        value: '1',
                    },
                    {
                        name: '2',
                        value: '2',
                    },
                    {
                        name: '3',
                        value: '3',
                    },
                ],
            },
            {
                id: 'TX',
                name: 'TX',
                settingType: SettingType.PIN,
                meta: 'TX',
                required: true,
                default: '',
            },
            {
                id: 'RX',
                name: 'RX',
                settingType: SettingType.PIN,
                meta: 'RX',
                required: true,
                default: '',
            },
            {
                id: 'RTS',
                name: 'RTS',
                settingType: SettingType.PIN,
                meta: 'RTS',
                required: false,
                default: '',
            },
            {
                id: 'CTS',
                name: 'CTS',
                settingType: SettingType.PIN,
                meta: 'CTS',
                required: false,
                default: '',
            },
            {
                id: 'DTR',
                name: 'DTR',
                settingType: SettingType.PIN,
                meta: 'INT',
                required: false,
                default: '',
            },
            {
                id: 'DSR',
                name: 'DSR',
                settingType: SettingType.PIN,
                meta: 'INT',
                required: false,
                default: '',
            },
            {
                id: 'MODE',
                name: 'Mode',
                settingType: SettingType.HIDDEN,
                required: true,
                default: 'PL_SER_MODE_UART',
                options: [
                    {
                        name: 'UART',
                        value: 'PL_SER_MODE_UART',
                    },
                    {
                        name: 'Wiegand',
                        value: 'PL_SER_MODE_WIEGAND',
                    },
                    {
                        name: 'Clock/Data',
                        value: 'PL_SER_MODE_CLOCKDATA',
                    },
                ],
            },
            {
                id: 'SERMODE',
                name: 'SerialMode',
                settingType: SettingType.OPTION,
                required: true,
                default: 'RS232',
                options: [
                    {
                        name: 'RS232',
                        value: 'RS232',
                    },
                    {
                        name: 'RS485',
                        value: 'RS485',
                    },
                    {
                        name: 'RS422',
                        value: 'RS422',
                    },
                ],
            },
            {
                id: 'BAUD',
                name: 'Baud Rate',
                settingType: SettingType.OPTION,
                required: true,
                default: '9600',
                linkToSetting: true,
                options: [
                    {
                        name: '1200',
                        value: '1200',
                    },
                    {
                        name: '2400',
                        value: '2400',
                    },
                    {
                        name: '4800',
                        value: '4800',
                    },
                    {
                        name: '9600',
                        value: '9600',
                    },
                    {
                        name: '19200',
                        value: '19200',
                    },
                    {
                        name: '38400',
                        value: '38400',
                    },
                    {
                        name: '57600',
                        value: '57600',
                    },
                    {
                        name: '115200',
                        value: '115200',
                    },
                    {
                        name: '230400',
                        value: '230400',
                    },
                    {
                        name: '460800',
                        value: '460800',
                    },
                    {
                        name: '921600',
                        value: '921600',
                    },
                ],
                setting: {
                    SETTINGID: 'BAUD',
                    NAME: 'Baud Rate',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MIN: 0,
                    MAX: 8,
                    DEFAULT: '9600',
                },
                UIControl: {
                    CONTROL: 'STATIC',
                    OPTIONS: [
                        {
                            name: '1200',
                            value: '1200',
                        },
                        {
                            name: '2400',
                            value: '2400',
                        },
                        {
                            name: '4800',
                            value: '4800',
                        },
                        {
                            name: '9600',
                            value: '9600',
                        },
                        {
                            name: '19200',
                            value: '19200',
                        },
                        {
                            name: '38400',
                            value: '38400',
                        },
                        {
                            name: '57600',
                            value: '57600',
                        },
                        {
                            name: '115200',
                            value: '115200',
                        },
                        {
                            name: '230400',
                            value: '230400',
                        },
                        {
                            name: '460800',
                            value: '460800',
                        },
                        {
                            name: '921600',
                            value: '921600',
                        },
                    ],
                },
            },
            {
                id: 'FLOWCONTROL',
                name: 'Flow Control',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
                linkToSetting: true,
                options: [
                    {
                        name: 'disabled',
                        value: '0',
                    },
                    {
                        name: 'RTSCTS',
                        value: '1',
                    },
                    {
                        name: 'XONOFF',
                        value: '2',
                    },
                ],
                setting: {
                    SETTINGID: 'FLOWCTRL',
                    NAME: 'Flow Control',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MIN: 1,
                    MAX: 1,
                    DEFAULT: '0',
                },
                UIControl: {
                    CONTROL: 'STATIC',
                    OPTIONS: [
                        {
                            name: 'disabled',
                            value: '0',
                        },
                        {
                            name: 'RTSCTS',
                            value: '1',
                        },
                        {
                            name: 'XONOFF',
                            value: '2',
                        },
                    ],
                },
            },
            {
                id: 'PARITY',
                name: 'Parity',
                settingType: SettingType.OPTION,
                required: true,
                linkToSetting: true,
                default: '0',
                options: [
                    {
                        name: 'None',
                        value: '0',
                    },
                    {
                        name: 'Even',
                        value: '1',
                    },
                    {
                        name: 'Odd',
                        value: '2',
                    },
                    {
                        name: 'Mark (Always 1)',
                        value: '3',
                    },
                    {
                        name: 'Space (Always 0)',
                        value: '4',
                    },
                ],
                setting: {
                    SETTINGID: 'PARITY',
                    NAME: 'Parity',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MIN: 1,
                    MAX: 1,
                    DEFAULT: '0',
                },
                UIControl: {
                    CONTROL: 'STATIC',
                    OPTIONS: [
                        {
                            name: 'None',
                            value: '0',
                        },
                        {
                            name: 'Even',
                            value: '1',
                        },
                        {
                            name: 'Odd',
                            value: '2',
                        },
                        {
                            name: 'Mark(Always 1)',
                            value: '3',
                        },
                        {
                            name: 'Space(Always 0)',
                            value: '4',
                        },
                    ],
                },
            },
            {
                id: 'BITS',
                name: 'Data Bits',
                settingType: SettingType.OPTION,
                required: true,
                linkToSetting: true,
                default: '1',
                options: [
                    {
                        name: '7 Bits',
                        value: '0',
                    },
                    {
                        name: '8 Bits',
                        value: '1',
                    },
                ],
                setting: {
                    SETTINGID: 'BITS',
                    NAME: 'Data Bits',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MIN: 1,
                    MAX: 1,
                    DEFAULT: '1',
                },
                UIControl: {
                    CONTROL: 'STATIC',
                    OPTIONS: [
                        {
                            name: '7 Bits',
                            value: '0',
                        },
                        {
                            name: '8 Bits',
                            value: '1',
                        },
                    ],
                },
            },
            {
                id: 'TXBUF',
                name: 'TX Buffer Pages',
                settingType: SettingType.BUFFER,
                required: true,
                default: '1',
            },
            {
                id: 'RXBUF',
                name: 'RX Buffer Pages',
                settingType: SettingType.BUFFER,
                required: true,
                default: '1',
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getCodeBlocks() {
        const blocks = [];
        const FLOWCONTROL = this.getSettingValue('FLOWCONTROL');
        const serName = `SER_${this.getSettingValue('NAME')}`;
        const NAME = this.getSettingValue('NAME');
        let flowControlMap = '';
        let wiegandSpecificCode = '';
        const project = this.project;

        if (FLOWCONTROL === '1') { // RTSCTS
            let interruptEnum = '0';
            try {
                const interruptNumber = Peripherals.getInterrupt(this.getSettingValue('CTS'), this.project.device.id);
                interruptEnum = PlatformUtils.getEnum(interruptNumber, 'pl_int_num', this.project.device.platform);
            } catch (ex) {
                //
            }
            flowControlMap = `
    ser.rtsmap=${Peripherals.getPinConstant(this.getSettingValue('RTS'), this.project.device.id)}
    io.num=${Peripherals.getPinConstant(this.getSettingValue('RTS'), this.project.device.id)}
    io.enabled=YES
    ser.ctsmap=${interruptEnum}
    io.num=${Peripherals.getPinConstant(this.getSettingValue('CTS'), this.project.device.id)}
    io.enabled=NO`;
        }
        if (this.getSettingValue('MODE') !== 'PL_SER_MODE_WIEGAND') {
            wiegandSpecificCode = `
    ${flowControlMap}
    ser.parity=${project.convertDataType(this.getSettingValue('PARITY'), TibboDataType.STRING, TibboDataType.BYTE)}
    ser.bits=${project.convertDataType(this.getSettingValue('BITS'), TibboDataType.STRING, TibboDataType.BYTE)}
    ser.flowcontrol=${project.convertDataType(this.getSettingValue('FLOWCONTROL'), TibboDataType.STRING, TibboDataType.BYTE)}
    dim div9600_${serName} as dword = ser.div9600
    ser.baudrate=div9600_${serName}*9600/${project.convertDataType(this.getSettingValue('BAUD'), TibboDataType.STRING, TibboDataType.DWORD)}
    ser.txbuffrq(${this.getSettingValue('TXBUF')})
    ser.rxbuffrq(${this.getSettingValue('RXBUF')})
    sys.buffalloc()
    ser.enabled=YES`;
        } else {
            wiegandSpecificCode = `ser.rxbuffrq(${this.getSettingValue('RXBUF')})
    ser.txbuffrq(${this.getSettingValue('TXBUF')})
    sys.buffalloc()
    ser.interchardelay=5
    ser.enabled=YES
    ser.rxclear()`;
        }

        let serSetupCode = '';
        if (this.getSettingValue('SERMODE') === 'RS485') {
            serSetupCode = `

    io.num=PL_IO_NUM_18_HDFD
    io.enabled=YES
    io.state=LOW

    io.num=PL_IO_NUM_17_RSMD
    io.enabled=YES
    io.state=HIGH

    io.num=PL_IO_NUM_3_TX1_INT3
    io.enabled=YES
    io.state=HIGH

    ser.dircontrol=PL_SER_DCP_LOWFORINPUT
    ser.interface=PL_SER_SI_HALFDUPLEX
    ser.rtsmap=PL_IO_NUM_3_TX1_INT3
    `;
        } else if (this.getSettingValue('SERMODE') === 'RS422') {
            serSetupCode = `
    io.num=PL_IO_NUM_18_HDFD
    io.enabled=YES
    io.state=HIGH

    io.num=PL_IO_NUM_17_RSMD
    io.enabled=YES
    io.state=HIGH
    ser.interface=PL_SER_SI_FULLDUPLEX
            `;
        } else if (this.getSettingValue('SERMODE') === 'RS232') { // RS232
            if (this.project.device.id === 'DS1102N') {
                serSetupCode = `
    io.num=PL_IO_NUM_18_HDFD
    io.enabled=YES
    io.state=HIGH

    io.num=PL_IO_NUM_17_RSMD
    io.enabled=YES
    io.state=LOW
    ser.interface=PL_SER_SI_FULLDUPLEX
            `;
            }
        }

        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `
    'serial port setup for port ${Number(this.getSettingValue('PORT'))}
    ser.num=${serName}
    ser.enabled=NO
    ser.mode=${this.getSettingValue('MODE')}
    ${serSetupCode}
    ${wiegandSpecificCode}
    
            `,
        });
        blocks.push({
            location: 'GLOBAL_CONSTS',
            blockType: BlockType.CODE,
            template: `
const ${serName}=${this.getSettingValue('PORT')}
            `,
        });

        blocks.push({
            location: 'on_ser_data_arrival',
            blockType: BlockType.CODE,
            template: `
    if ser.num=${serName} then
        'data on serial port ${this.getSettingValue('NAME')}
    end if
        `,
        });

        return blocks;
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        if (settingId === 'SERMODE') {
            if (this.project.device.id === 'DS1101N') {
                return [
                    {
                        name: 'RS232',
                        value: 'RS232',
                    },
                ];
            }
        }
        return super.getSettingOptions(settingId);
    }

    getState(settingId: string): SettingState {
        if (this.getSettingValue('MODE').toString() === 'PL_SER_MODE_WIEGAND') {
            switch (settingId) {
                case 'BAUD':
                case 'FLOWCONTROL':
                case 'PARITY':
                case 'BITS':
                    return SettingState.DISABLED;
                default:
                    return SettingState.ENABLED;
            }
        }
        return SettingState.ENABLED;
    }

    getDetails(): string {
        return 'https://docs.tibbo.com/taiko/object_ser';
    }
}
